'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';

export function FeaturedBlogs({ blogs }: { blogs: Blog[] }) {
  const locale = useLocaleStore(state => state.currentLocale);
  const carouselContext = useCarouselContext();
  const { t } = useTranslations();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;
  const activeBlog = blogs[selectedIndex] ?? blogs[0];

  if (!activeBlog) return null;

  let formattedDate: string;
  try {
    formattedDate = new Date(activeBlog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(activeBlog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <section className={cn('custom-section', 'pb-8 pt-4 sm:py-12 md:py-14 lg:py-16 xl:py-[72px]')}>
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col-reverse gap-8 sm:gap-10',
          'md:flex-row md:items-stretch md:justify-between md:gap-16',
        )}
      >
        <div
          className={cn(
            'ea-featured-blog__content',
            'flex max-w-[573px] flex-col justify-between gap-8',
            'min-h-0 md:flex-1',
          )}
        >
          <div className="flex flex-col items-start justify-start gap-8">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                {activeBlog?.categories && activeBlog.categories.length > 0 && (
                  <span className="flex h-[29px] items-center justify-center rounded-full bg-primary px-4 text-xs font-medium text-white">
                    {activeBlog.categories[0].name}
                  </span>
                )}
                <p className="text-xs text-[#666666] sm:text-sm lg:text-base">{formattedDate}</p>
              </div>

              <h2
                className={cn(
                  'line-clamp-2 text-[40px] font-medium leading-[1.1] tracking-tighter',
                  'sm:text-5xl',
                  'lg:text-[56px]',
                )}
              >
                {activeBlog?.name}
              </h2>
              <Paragraph innerHtml={activeBlog?.content} className="line-clamp-3" />
            </div>
            <Button asChild variant={'outline'} className="text-black w-full sm:w-fit">
              <Link prefetch={false} href={ROUTES.BLOG_DETAIL({ slug: activeBlog.slug })}>
                {t('text1148')} <HiOutlineArrowRightCircle size={24} />
              </Link>
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <CarouselControls 
              className={{
                main: 'flex items-center justify-start gap-2',
                button: 'max-w-12 min-w-12 max-h-12 min-h-12 sm:min-h-[52px] sm:min-w-[52px] sm:max-w-[52px] sm:max-h-[52px] bg-black text-white hover:bg-black/90 transition-all border-none shadow-none',
              }}
            />
            <div
              className={cn(
                'flex items-center justify-center gap-2',
                'ea-carousel__dots__container',
              )}
            >
              {blogs.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex h-2 w-2 items-center justify-start rounded-full bg-[#E5E7EB] transition-all',
                    {
                      'w-10': selectedIndex === index,
                    },
                  )}
                >
                  {selectedIndex === index && (
                    <div className="h-full w-[19px] rounded-full bg-[#000000] transition-all" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="w-full max-w-[692px]">
          <Carousel
            enableInterval
            intervalLength={4000}
            enableLoop
            className={{
              container: 'w-full',
              main: 'w-full',
              slide: 'w-full',
              wrapper: 'w-full',
            }}
            slides={blogs.map((blog, index) => (
              <Image
                key={blog.slug}
                src={blog?.image?.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(blog?.image, blog?.name)}
                width={0}
                height={0}
                className={cn(
                  'aspect-square w-full max-w-[692px] rounded-[24px] object-cover object-center',
                  'md:h-full md:w-full',
                )}
                sizes="(max-width: 768px) 100vw, 50vw"
                priority={index === 0}
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : undefined}
              />
            ))}
          />
        </div>
      </div>
    </section>
  );
}
